import type { TripSummaryItem } from '@/scopes/prices/domain/transports/types'

import { useIntl } from '@/intl'
import { transportResponseSelector } from '@/scopes/prices/components/selectors'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'
import { mqMin, space } from '@sunwebgroup/ui-components'
import { Icon, IconGlyph, Rating, Typography } from '@sunwebgroup/ui-components/uds'
import { styled } from 'styled-components'

const ExtraInfoText = styled(Typography)`
  display: block;
  margin-left: ${space('primitives_space_28')};
`

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  ${Icon} {
    margin-right: ${space('primitives_space_4')};
  }
`

const Stars = styled(Rating)`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-left: auto;
  @media ${mqMin('primitives_breakpoint_m_min')} {
    margin-left: ${space('primitives_space_4')};
  }
`

const UDS_MAP: Record<string, IconGlyph> = {
  IconBuilding: 'AppartmentBuilding',
  IconCalendar: 'Calendar',
  IconFamily: 'Family',
  IconAirplaneDeparture: 'AirplaneTakingOff',
  IconAirplaneReturn: 'AirplaneReturn',
  IconCarFront: 'CarFront',
  IconBusRight: 'BusRight',
}

function Item({ header, extraInfo }: TripSummaryItem) {
  const glyph = UDS_MAP[header.icon]

  return (
    <div>
      <HeaderWrapper>
        <Icon glyph={glyph ?? 'CheckMark'} />
        <Typography variant="body-m-bold-tight">{header.text}</Typography>
        {!!header.stars && <Stars rating={header.stars} />}
      </HeaderWrapper>
      {extraInfo.map((text, index) => (
        <ExtraInfoText variant="body-m-regular-tight" key={index}>
          {text}
        </ExtraInfoText>
      ))}
    </div>
  )
}

const Wrapper = styled.div`
    ${Typography}:first-child {
      margin: ${space('primitives_space_16')} 0;
      display: inline-block;
    }
`

const ItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_12')};
`

export function TripSummary() {
  const { tripSummary } = usePricesSelector(transportResponseSelector)
  const intl = useIntl()
  return (
    <Wrapper>
      <Typography variant="body-l-bold-tight">{intl.formatMessage({ id: 'price_basket_trip_summary_title' })}</Typography>
      <ItemWrapper>
        {tripSummary?.map((item, index) => <Item key={index} {...item} />)}
      </ItemWrapper>
    </Wrapper>
  )
}
